import { useFormik } from "formik";
import { Toast } from "primereact/toast";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useIsMobile } from "../../../../Utils/ResizeUtils";
import CommonToolbar from "../../../UIElements/CommonToolbar";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import { useLocation, useNavigate } from "react-router-dom";
import { Button } from "primereact/button";
import { TabMenu } from "primereact/tabmenu";

import commonStyle from "../../CommonStyles.module.css";
import EnumsContext from "../../../../context/enums-context";
import { formatQuantityWithMeasurement, formatShortDate } from "../../../../Utils/Utils";
import useGetEnums from "../../../../Utils/EnumsUtils";
import { Toolbar } from "primereact/toolbar";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import useApi from "../../../../Utils/BackendClient";
import { DisplayErrors } from "../../../../Utils/ErrorHandler";
import HistoryElement from "../../../UIElements/HistoryElement";
import DropdownInput from "../../../UIElements/Inputs/DropdownInput";
import CalendarInput from "../../../UIElements/Inputs/CalendarInput";
import ActNumberWithAutogenerateInput from "../../../UIElements/Inputs/ActNumberWithAutogenerateInput";
import RemainingPickerSidebar from "../../../UIElements/Sidebars/RemainingPickerSidebar";
import { numberEditor } from "../../../../Utils/TableEditors";

const ProductsTransferDetails = () => {

    const ctx = useContext(EnumsContext);
    const isMobile = useIsMobile();
    const location = useLocation();
    const navigate = useNavigate();
    const toast = useRef();

    const { createMoveRemainingsAct, fetchMoveRemainingActDetails, fetchMoveRemainingHistory, updateMoveRemainingAct } = useApi();

    const enumTypes = useMemo(() => ["divisions"], []);

    useGetEnums(enumTypes);

    const [actData, setActData] = useState({
        outcome_division_id: null,
        division_id: null,
        act_number: '',
        act_date: new Date(),
        act_number_autogenerate: true,
        unidentified_products: [],
        identified_products: []
    });
    const [actHistory, setActHistory] = useState(null);
    const [isEdit, setIsEdit] = useState(false);
    const [isFinalized, setIsFinalized] = useState(false);
    const [historyLazyState, setHistoryLazyState] = useState({
        first: 0,
        rows: 10,
        page: 1   
    });
    const [historyTotalRecords, setHistoryTotalRecords] = useState(0);
    const [showActBaseOnMobile, setShowActBaseOnMobile] = useState(true);
    const [submitLoading, setSubmitLoading] = useState(false);
    const [tabShowed, setTabShowed] = useState(0);
    const [visible, setVisible] = useState(false);

    useEffect(() => {
        if (location.state !== null) {

            setIsEdit(true);

            const fetchData = async () => {
                const response = await fetchMoveRemainingActDetails(location.state.id);

                if (response.status === 200) {

                    setIsFinalized(response.data.status === "FINALIZED");

                    response.data.act_date = new Date(response.data.act_date);
                    console.log(response.data);
                    setActData(response.data);
                }
            }

            fetchData();
        }
    },[fetchMoveRemainingActDetails, location]);

    const formik = useFormik({
        initialValues: actData,
        enableReinitialize: true,
        validate: values => {
            const errors = {};

            if (!values.outcome_division_id) {
                errors.outcome_division_id = "Виберіть точку продажу";
            }

            if (!values.division_id) {
                errors.division_id = "Виберіть цільову точку призначення";
            }

            if (values.division_id === values.outcome_division_id) {
                errors.division_id = "Точка придажу та точка продажу призначення не повинні співпадати";
                errors.outcome_division_id = "Точка придажу та точка продажу призначення не повинні співпадати";
            }

            if (!values.act_number_autogenerate) {
                if (values.act_number === null || values.act_number === "") {
                    errors.act_number = "Введіть номер акту";
                }
            }
            return errors;
        },
        onSubmit: async (values) => {

            setSubmitLoading(true);

            const payload = {
                ...values,
                act_date: formatShortDate(values.act_date),
                identified_products: values.identified_products.map(product => ({
                    remaining_id: product.remaining_id ?? product.id
                })),
                unidentified_products: values.unidentified_products.map(product => (product.product_id ? {
                    remaining_id: product.id,
                    quantity: product.quantity,
                    note: product.note
                } : product)),
            };

            const response = isEdit ? await updateMoveRemainingAct(location.state?.id, payload) : await createMoveRemainingsAct(payload);

            const successStatus = isEdit ? 200 : 201;

            setSubmitLoading(false);

            if(response.status === successStatus)
            {
                const toastMessage = isEdit ? 
                    {severity: "success", summary: "Успіх редагування", detail: `Акт №${location.state?.id} успішно відредаговано`, life: 3000} :
                    {severity: "success", summary: "Успіх створення", detail: "Акт успішно створено", life: 3000};

                navigate("/productstransfer/", { state: { toast: toastMessage } });
            }
            else
            {
                DisplayErrors(response.error, toast);
            }
        }
    });

    const getActHistory = async (id, state) => {

        const response = await fetchMoveRemainingHistory(id, state);

        if (response.status === 200) {
            setActHistory(response.data.results);
            setHistoryTotalRecords(response.data.count);
        }
        else {
            DisplayErrors(response.error, toast);
        }
    }

    const confirmLeave = (event) => {

        if (formik.values !== actData) {

            confirmDialog({
                group: 'headless',
                header: 'Незбережені зміни',
                message: 'У вас є незбережені зміни. Ви впевнені, що хочете залишити цю сторінку?',
                icon: 'pi pi-exclamation-triangle',
                acceptLabel: "Так",
                rejectLabel: "Hi",
                defaultFocus: 'accept',
                accept: () => { navigate("/productstransfer", { state: { pageState: location?.state?.pageState } }) }
            });

        }
        else {
            navigate("/productstransfer", { state: { pageState: location?.state?.pageState } });
        }
    }

    const AddProductHandler = () => {
        setVisible(true);
    }

    const toolbarLeftData = () => {

        let name = "Створення нового акту переміщення";

        if (isEdit) {
            name = "Редагування акту переміщення №" + formik.values.act_number;
            if (isFinalized) {
                name = "Інформація про акт переміщення №" + formik.values.act_number;
            }
        }

        return !isMobile ?
            <h3>{name}</h3> :
            (!isFinalized ? <Button type="button" icon={`pi ${showActBaseOnMobile ? "pi-chevron-circle-up" : "pi-chevron-circle-down"}`} severity="secondary" outlined onClick={() => setShowActBaseOnMobile(!showActBaseOnMobile)} /> : null);
    }

    const DeleteFieldHandler = (rowData, type) => {

        const updatedItems = formik.values[type].filter(item => item.id !== rowData.id);

        formik.setFieldValue(type, updatedItems);
    };

    const items = [
        {
            label: isMobile ? "Деталі" : "Деталі акту",
            icon: 'pi pi-book',
            command: () => {
                setTabShowed(0);
            },
        },
        ...(isEdit
            ? [
                {
                    label: 'Історія',
                    icon: 'pi pi-history',
                    command: () => {
                        setTabShowed(1);
                        getActHistory(location.state.id, historyLazyState);
                    },
                }
            ]
            : []),
    ];

    const onHistoryPage = (event) => {
        event.page = event.page + 1;
        getActHistory(location.state.id, event);

        setHistoryLazyState(event);
    }

    const handleCellEditComplete = (e, arrayName) => {
        const rowIndex = e.index ?? e.rowIndex;

        const updated = [...formik.values[arrayName]];
        updated[rowIndex] = {
            ...updated[rowIndex],
            [e.field]: e.newValue
        };

        formik.setFieldValue(arrayName, updated);
    };

    const contentToolbarRightTemplate = () => {
        return (<>
            <div className="flex flex-wrap gap-2">
                <Button label={`${isMobile ? "" : "+Додати товар"}`} icon={`${isMobile ? "pi pi-plus" : null}`} severity="success" className={commonStyle.addButton} type="button" onClick={() => AddProductHandler()} />
            </div>
        </>);
    }

    const setTableHeader = (tablename) => {
        return (
            <div className="flex flex-wrap text-align-center align-items-center">
                <span className={!isMobile ? "text-xl text-900 font-bold" : "font-bold"}>{tablename}</span>
            </div>
        );
    }

    const tableActionBodyTemplate = (rowData, type) => {

        return (<div className="flex flex-wrap gap-2">
            {!isFinalized && <Button
                icon="pi pi-trash"
                type="button"
                className={`p-button-rounded p-button-danger ${commonStyle.deleteButton}`}
                tooltip="Видалити з таблиці"
                tooltipOptions={{ showOnDisabled: true }}
                onClick={() => DeleteFieldHandler(rowData, type)}
            />}
        </div>
        );
    };

    const AddProductToTable = (product) => {
        if (product.identifier) {
            let identifiedProducts = formik.values.identified_products;
            identifiedProducts = [...identifiedProducts, product];
            formik.setFieldValue("identified_products", identifiedProducts);
        }
        else {
            let unidentifiedProducts = formik.values.unidentified_products;
            unidentifiedProducts = [...unidentifiedProducts, product];
            formik.setFieldValue("unidentified_products", unidentifiedProducts);
        }
    }

    return (
        <form onSubmit={formik.handleSubmit}>
            <Toast ref={toast} />
            <CommonToolbar isMobile={isMobile} isFinalized={isFinalized} loading={submitLoading} onRetunButtonClick={(event) => confirmLeave(event)} leftContent={toolbarLeftData()} />
            <ConfirmDialog group="headless" />
            <TabMenu model={items} />
            {tabShowed === 0 && <div className="grid justify-content-center">
                <div className={`${!isMobile ? commonStyle.leftMenu : commonStyle.mobileActDetailsMenu} ${isMobile ? "col-12 flex flex-column align-items-center" : "col-3"}`}>
                    <DropdownInput 
                        formik={formik}
                        paramName={"outcome_division_id"}
                        context={ctx.enums.divisions.data}
                        onChange={(val) => formik.setFieldValue("outcome_division_id", val)}
                        label={"Точка продажу"}
                        isDisabled={isFinalized}
                        isMobile={isMobile} 
                        mandatory/>
                    <DropdownInput 
                        formik={formik}
                        paramName={"division_id"}
                        context={ctx.enums.divisions.data}
                        onChange={(val) => formik.setFieldValue("division_id", val)}
                        label={"Точка продажу призначення"}
                        isDisabled={isFinalized}
                        isMobile={isMobile} 
                        mandatory/>
                    <CalendarInput formik={formik} paramName={"act_date"} label={"Дата акту"} isDisabled={isFinalized} isMobile={isMobile}/>
                    <ActNumberWithAutogenerateInput formik={formik} showAutogenerate={!isFinalized} isDisabled={isFinalized}/>
                </div>
                <div className={`${isMobile ? "col-12" : "col-9"}`}>
                    {!isFinalized && <Toolbar className="mb-4" end={contentToolbarRightTemplate} />}
                    {formik.values.identified_products.length > 0 && <DataTable value={formik.values.identified_products} header={setTableHeader("Товари індивідуального обліку")}
                        tableStyle={{ minWidth: '50rem' }}>
                        <Column field="product_name" header="Продукт" style={{ width: '25%' }} />
                        <Column field="note" header="Примітка" style={{ width: '15%' }} />
                        <Column field="identifier" header="Ідентифікатор" style={{ width: '15%' }} />
                        <Column field="action" body={rowData => tableActionBodyTemplate(rowData, "identified_products")} style={{ width: '15%' }} />
                    </DataTable>}
                    {formik.values.unidentified_products.length > 0 && <DataTable value={formik.values.unidentified_products} editMode="cell" header={setTableHeader("Товари поштучного обліку")}
                        tableStyle={{ minWidth: '50rem' }}>
                        <Column field="product_name" header="Продукт" style={{ width: '25%' }} />
                        <Column field="note" header="Примітка" style={{ width: '15%' }} />
                        <Column field="quantity" header="Кількість" 
                            editor={(options) => numberEditor(options, options.rowData.measurement_units === "PIECES" ? 0 : 2)} onCellEditComplete={(e) => handleCellEditComplete(e, "unidentified_products")}
                            body={(rowData) => formatQuantityWithMeasurement(rowData.quantity, rowData.measurement_units)} style={{ width: '15%' }} />
                        <Column field="action" body={rowData => tableActionBodyTemplate(rowData, "unidentified_products")} style={{ width: '15%' }} />
                    </DataTable>}
                </div>
            </div>}
            {tabShowed === 1 && <HistoryElement data={actHistory} lazyState={historyLazyState} totalRecords={historyTotalRecords} onHistoryPage={onHistoryPage} isAct={true}/>}
            <RemainingPickerSidebar visible={visible} onHide={() => setVisible(false)} isModile={isMobile} toast={toast} formik={formik} ctx={ctx} onProductPick={(prod => AddProductToTable(prod))} divisionField="outcome_division_id"/>
        </form>
    )
}

export default ProductsTransferDetails;